import { find, get } from 'lodash';
import { functions } from '@enums/asynchronous-functions';
import { jobStatuses } from '@enums/jobapi';
import { payloadKeySeparator } from '@enums/notifications';
import { getMappedJobType } from '@sharedModules/data/utils/notification-utils';

const getTranslationKey = ({ mappedJobType, payload, payloadKey }) => {
  const { baseTranslation } = get(functions, mappedJobType, {});
  if (
    payloadKey === `workpackage_id${payloadKeySeparator}skip_optimisation` &&
    payload.split(payloadKeySeparator)[1] === 'true'
  ) {
    const { skipOptimisationTranslation } = get(functions, mappedJobType, {});
    return skipOptimisationTranslation || baseTranslation;
  }
  return baseTranslation;
};

const handleMessage = ({ e, rootState, commit, dispatch, storeContext }) => {
  const { lastEventId, data: message } = e;

  // blank messages are sent intermittently to keep the EventSource open
  if (message === '') {
    return;
  }
  const [jobId, jobType] = lastEventId.split(':');
  const mappedJobType = getMappedJobType(jobType);
  const [jobStatus, payload] = message.split(':');
  // notification payload is sent via handleJobExit webtool/server/src/api/webhooks/webhooks.controller.js
  // --- PRE chart v0.41.0 ---
  // Analytics notification payload is sent in status_updaters.py HandlerFactory class
  // https://owlabs.atlassian.net/wiki/spaces/RTLS/pages/4347494520/Analytics+notifications+after+JobApi+removal

  let notificationPayload = payload; // if the payload isn't a document key, use it as is
  let id; // may need the id in after hooks. e.g. afterDeleteWorkpackage.

  const payloadKey = get(functions, [mappedJobType, 'payloadKey']);

  // if payload is a database key, add a check here
  if (payloadKey === 'scenario_key') {
    const { allScenariosForWorkpackage: scenarioMetadataArray } = rootState.scenarioMetadata;
    const scenarioMetadata = find(scenarioMetadataArray, { scenarioKey: payload });
    notificationPayload = get(scenarioMetadata, 'scenarioDescription');
  }

  if (
    [
      'workpackage_id',
      'destination_workpackage_id',
      `workpackage_id${payloadKeySeparator}level_entry_key`,
      `workpackage_id${payloadKeySeparator}level_entry_keys`,
      `workpackage_id${payloadKeySeparator}skip_optimisation`,
    ].includes(payloadKey)
  ) {
    let workpackageId = payload;
    if (payloadKey.includes(payloadKeySeparator)) {
      workpackageId = payload.split(payloadKeySeparator)[0];
    }
    const workpackage = find(rootState.workpackages.workpackages, { _id: workpackageId });
    notificationPayload = workpackage
      ? workpackage.description
      : rootState.workpackages.selectedWorkpackage.description;
    id = payload;
  }

  if (
    jobStatus === jobStatuses.sentToJobApiFromCreation &&
    mappedJobType.startsWith('run_pricing_for_all')
  ) {
    notificationPayload = payload;
  }

  if (jobStatus === 'progress') {
    // frontend relies on jobType key to display progress notifications
    const progressNotification = { jobId, jobType: mappedJobType, jobStatus, payload };
    return commit('addProgressNotification', progressNotification);
  }

  const baseTranslation = getTranslationKey({
    mappedJobType,
    payload,
    payloadKey,
  });
  // create a notification if a translation exists for the job type
  if (baseTranslation) {
    const notification = {
      id: `${jobId}:${jobStatus}:${Date.now()}`,
      jobStatus,
      notificationPayload,
      baseTranslation,
    };
    commit('addNotification', notification);
  }
  // if an action is defined for the function/jobStatus combination, dispatch it
  const action = get(functions, [mappedJobType, 'actions', jobStatus]);
  if (action) {
    dispatch(action, { jobStatus, id, payload }, { root: true });
  }
  // if an event is defined for the function/jobStatus combination, trigger it
  const event = get(functions, [mappedJobType, 'events', jobStatus]);
  if (event) {
    // Use the stored store context to access Vue instance
    // eslint-disable-next-line no-underscore-dangle
    if (storeContext._vm && storeContext._vm.$eventBus) {
      // eslint-disable-next-line no-underscore-dangle
      storeContext._vm.$eventBus.$emit(event, { jobStatus, id, payload });
    }
  }
};

const store = {
  namespaced: true,

  state: {
    progressNotifications: [],
    notifications: [],
    notificationLimit: 3,
    eventStream: null,
  },

  getters: {
    notifications: state => state.notifications.slice(-state.notificationLimit),
    progressNotifications: state => state.progressNotifications.slice(-state.notificationLimit),
  },

  mutations: {
    addNotification(state, notification) {
      state.notifications.push(notification);
    },

    addProgressNotification(state, progressNotification) {
      state.progressNotifications.push(progressNotification);
    },

    setEventStream(state, { eventStream }) {
      state.eventStream = eventStream;
    },

    resetNotifications(state) {
      // fully resetting notifications can cause the frontend to not display notifications.
      // instead keep last 10 notifications.
      state.notifications = state.notifications.slice(-10);
    },

    resetProgressNotifications(state) {
      state.progressNotifications = [];
    },
  },

  actions: {
    resetAllNotifications({ commit }) {
      commit('resetNotifications');
      commit('resetProgressNotifications');
    },
    resetProgressNotifications({ commit }) {
      commit('resetProgressNotifications');
    },
    openNotificationStream({ rootState, commit, dispatch, state }) {
      let reconnectFrequencySeconds = 1; // first attempt to reconnect after 1 second
      let reconnectCount = 0;
      let source;

      // Store reference to the store context for accessing Vue instance in this._vm
      const storeContext = this;

      // reconnection handling from https://stackoverflow.com/a/54385402
      function throttle(func) {
        let timeout;
        return () => {
          clearTimeout(timeout);
          timeout = setTimeout(() => {
            timeout = null;
            func();
          }, reconnectFrequencySeconds * 1000);
        };
      }

      // attempt reconnection every `reconnectFrequencySeconds`
      const reconnectFunc = throttle(() => {
        reconnectCount += 1;

        // Force a token refresh, as this is the main reason why eventstream connections drops
        // Only force resfresh every 5th connection attempt, to avoid refreshing the token just after another refresh
        if (reconnectCount > 1 && reconnectCount % 5 === 0) {
          dispatch('context/refreshUserContext', {}, { root: true });
        }

        // eslint-disable-next-line no-use-before-define
        setupEventSource();

        // Double every attempt to avoid overwhelming server
        reconnectFrequencySeconds = Math.min(reconnectFrequencySeconds * 2, 15);
      });

      function setupEventSource() {
        if (state.eventStream) {
          state.eventStream.close();
          commit('setEventStream', { eventStream: null });
        }
        source = new EventSource('/api/notifications');

        source.addEventListener('open', () => {
          reconnectCount = 0;
        });

        source.addEventListener('error', function() {
          // close the source (e.g. on token timeout)
          source.close();
          reconnectFunc();
        });

        source.addEventListener('message', function(e) {
          return handleMessage({ e, rootState, commit, dispatch, storeContext });
        });
        commit('setEventStream', { eventStream: source });
      }
      // initial setup
      setupEventSource();
    },
  },
};

export default store;
